import { useState, useMemo } from 'react';
import { useApi } from '../hooks/useApi.js';

interface DmarcReport {
  id: string;
  domain: string;
  reporting_org: string;
  date_range_begin: string;
  date_range_end: string;
  total_messages: number;
  passed_messages: number;
  pass_rate: number;
  policy_p: string;
}

interface ReportsResponse {
  data: DmarcReport[];
  hasMore?: boolean;
  total_messages: number;
  pass_rate: number;
}

interface SummaryResponse {
  data: {
    domain: string;
    pass_rate: number;
    total_messages: number;
    policy: string;
    trend: Array<{ date: string; pass_rate: number; total: number }>;
  };
}

export function DmarcReportsPage() {
  const [selectedDomain, setSelectedDomain] = useState('');
  const [period, setPeriod] = useState('');

  const reportParams = [
    selectedDomain && `domain=${encodeURIComponent(selectedDomain)}`,
    period && `period=${encodeURIComponent(period)}`,
  ].filter(Boolean).join('&');

  const { data: reportsData, loading, error, refresh } = useApi<ReportsResponse>(
    `/dmarc/reports${reportParams ? `?${reportParams}` : ''}`
  );
  const { data: summaryData } = useApi<SummaryResponse>(
    selectedDomain ? `/dmarc/summary?domain=${encodeURIComponent(selectedDomain)}` : null
  );

  const reports = reportsData?.data ?? [];
  const summary = summaryData?.data;

  const domains = useMemo(() => {
    const set = new Set(reports.map(r => r.domain));
    return Array.from(set).sort();
  }, [reports]);

  const totalMessages = reportsData?.total_messages ?? reports.reduce((s, r) => s + r.total_messages, 0);
  const passRate = reportsData?.pass_rate ?? (totalMessages > 0
    ? Math.round(reports.reduce((s, r) => s + r.passed_messages, 0) / totalMessages * 100)
    : 100);
  const sources = new Set(reports.map(r => r.reporting_org)).size;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading DMARC reports...</div></div>;
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-700">{error}</p>
        <button onClick={refresh} className="mt-2 text-sm text-red-600 underline">Retry</button>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">DMARC Reports</h2>
      <p className="text-gray-600 mb-6">Analyze DMARC aggregate reports to protect against email spoofing.</p>

      <div className="flex items-center gap-4 mb-6">
        <label className="text-sm font-medium text-gray-700">Domain:</label>
        <select
          value={selectedDomain}
          onChange={(e) => setSelectedDomain(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All Domains</option>
          {domains.map((d) => (
            <option key={d} value={d}>{d}</option>
          ))}
        </select>
        <label className="text-sm font-medium text-gray-700 ml-4">Period:</label>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
        >
          <option value="">All Time</option>
          <option value={new Date().toISOString().slice(0, 7)}>This Month</option>
          <option value={new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 7)}>Last Month</option>
        </select>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Messages</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{totalMessages.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Overall Pass Rate</p>
          <p className={`text-2xl font-bold mt-1 ${passRate >= 95 ? 'text-emerald-600' : passRate >= 80 ? 'text-yellow-600' : 'text-red-600'}`}>
            {passRate}%
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Reporting Sources</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{sources}</p>
        </div>
      </div>

      {summary?.trend && summary.trend.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-sm font-semibold text-gray-800 mb-4">Pass Rate Trend (30 days)</h3>
          <div className="flex items-end gap-1 h-32">
            {summary.trend.map((t) => (
              <div key={t.date} className="flex-1 flex flex-col items-center">
                <div
                  className={`w-full rounded-t ${t.pass_rate >= 95 ? 'bg-emerald-400' : t.pass_rate >= 80 ? 'bg-yellow-400' : 'bg-red-400'}`}
                  style={{ height: `${Math.max(t.pass_rate, 5)}%` }}
                  title={`${t.date}: ${t.pass_rate}% (${t.total} msgs)`}
                />
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-1">
            <span className="text-xs text-gray-400">{summary.trend[0]?.date}</span>
            <span className="text-xs text-gray-400">{summary.trend[summary.trend.length - 1]?.date}</span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Domain</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Period</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Messages</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Passed</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Pass Rate</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Policy</th>
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No DMARC reports found. Reports are collected automatically from your DMARC mailbox.</td></tr>
            ) : reports.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-medium text-gray-900">{r.domain}</td>
                <td className="px-4 py-3 text-gray-600">{r.reporting_org}</td>
                <td className="px-4 py-3 text-gray-500">{r.date_range_end?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right text-gray-600">{r.total_messages.toLocaleString()}</td>
                <td className="px-4 py-3 text-right text-gray-600">{r.passed_messages.toLocaleString()}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-medium ${r.pass_rate >= 98 ? 'text-emerald-600' : r.pass_rate >= 95 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {r.pass_rate}%
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    r.policy_p === 'reject' ? 'bg-emerald-100 text-emerald-700' :
                    r.policy_p === 'quarantine' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{r.policy_p}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
